import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────
interface TeamMember  { firstName: string; lastName: string; email: string | null; phone: string | null; handicap?: string | null; isCaptain: boolean; }
interface DinnerGuest { firstName: string; lastName: string; email: string | null; }
interface AddOn       { name: string; price?: number | null; }

interface ReceiptData {
  confirmationNumber: string;
  registrationTimestamp: string;
  registrationType: string;
  sponsorshipLevel: string | null;
  teamName: string | null;
  captainFirstName: string;
  captainLastName: string;
  captainEmail: string;
  captainPhone: string;
  captainCompany: string | null;
  totalAmount: number;
  donationAmount: number;
  cardLastFour: string | null;
  additionalSponsorships: AddOn[];
  teamMembers: TeamMember[];
  dinnerGuests: DinnerGuest[];
  logoUrl: string | null;
  specialRequests: string | null;
}

type ShareStatus = 'idle' | 'sharing' | 'success' | 'none' | 'error';

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const SPONSORSHIP_LABELS: Record<string, string> = {
  platinum:           'Platinum Sponsor',
  dinner:             'Dinner Sponsor',
  lunch:              'Lunch Sponsor',
  'beverage-cart':    'Beverage Cart Sponsor',
  'bloody-mary':      'Bloody Mary Breakfast Sponsor',
  'food-truck':       'Food Truck Sponsor',
  'air-cannon':       'Air Cannon Sponsor',
  'cigar-bar':        'Cigar Bar Sponsor',
  photography:        'Photography Sponsor',
  'raffle-prize':     'Raffle Prize Sponsor',
  'beverage-station': 'Beverage Station Sponsor',
  'golf-cart':        'Golf Cart Sponsor',
  'premier-hole':     'Premier Hole Sponsor',
  'driving-range':    'Driving Range Sponsor',
  'closest-pin':      'Closest to the Pin Sponsor',
  'longest-drive':    'Longest Drive Sponsor',
};

const REG_TYPE_LABELS: Record<string, string> = {
  golfer:              'Golfer Registration',
  'sponsor-golfer':    'Sponsor + Golfer',
  sponsor:             'Sponsorship',
  'dinner-only':       'Dinner Only',
  'silent-auction':    'Silent Auction',
  'monetary-donation': 'Monetary Donation',
  'add-golfers':       'Additional Golfers',
};

function fmt(n: number) {
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatDateTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' at '
      + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

function initials(first: string, last: string) {
  return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase();
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────
const ReceiptPage: React.FC = () => {
  const { confirmationNumber } = useParams<{ confirmationNumber: string }>();
  const [receipt, setReceipt]           = useState<ReceiptData | null>(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [shareStatus, setShareStatus]   = useState<ShareStatus>('idle');
  const [shareCount, setShareCount]     = useState(0);

  useEffect(() => {
    if (!confirmationNumber) { setError('No confirmation number provided.'); setLoading(false); return; }
    fetch(`/api/receipt?confirmation=${encodeURIComponent(confirmationNumber)}`)
      .then(r => r.json())
      .then(json => {
        if (json.success) setReceipt(json.data);
        else setError(json.error || 'Receipt not found.');
      })
      .catch(() => setError('Could not load receipt. Please try again.'))
      .finally(() => setLoading(false));
  }, [confirmationNumber]);

  // ── Share handler ─────────────────────────────────────────────────────────
  const handleShare = useCallback(async () => {
    if (!receipt) return;
    const receiptUrl = `${window.location.origin}/receipt/${receipt.confirmationNumber}`;

    // Collect non-captain participants with email addresses
    const recipients: Array<{ email: string; name: string }> = [];
    receipt.teamMembers.forEach(m => {
      if (!m.isCaptain && m.email) recipients.push({ email: m.email, name: `${m.firstName} ${m.lastName}` });
    });
    receipt.dinnerGuests.forEach(g => {
      if (g.email) recipients.push({ email: g.email, name: `${g.firstName} ${g.lastName}` });
    });

    if (recipients.length === 0) {
      setShareStatus('none');
      setTimeout(() => setShareStatus('idle'), 3500);
      return;
    }

    setShareStatus('sharing');
    let sent = 0;
    for (const r of recipients) {
      try {
        const res = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shareReceipt: true,
            recipientEmail: r.email,
            recipientName: r.name,
            senderName: `${receipt.captainFirstName} ${receipt.captainLastName}`,
            receiptUrl,
            confirmationNumber: receipt.confirmationNumber,
          }),
        });
        if (res.ok) sent++;
      } catch { /* continue */ }
    }
    setShareCount(sent);
    setShareStatus(sent > 0 ? 'success' : 'error');
    setTimeout(() => setShareStatus('idle'), 4000);
  }, [receipt]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.centered}>
          <div style={S.spinner} />
          <p style={{ color: '#6b7280', marginTop: 16, fontSize: 14 }}>Loading receipt...</p>
        </div>
        <style>{spinKeyframes}</style>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error || !receipt) {
    return (
      <div style={S.page}>
        <div style={S.mobileCard}>
          <img src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`} alt="Champion It Forward" style={{ height: 52, marginBottom: 20 }} />
          <div style={{ ...S.checkCircle, background: '#1f1f1f', boxShadow: 'none' }}>
            <span style={{ fontSize: 28 }}>X</span>
          </div>
          <h2 style={{ color: '#e51c22', fontSize: 18, fontWeight: 700, margin: '16px 0 8px', textAlign: 'center' }}>Receipt Not Found</h2>
          <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center' }}>{error}</p>
        </div>
      </div>
    );
  }

  const {
    confirmationNumber: confNum, registrationTimestamp,
    registrationType, sponsorshipLevel, teamName,
    captainEmail, captainCompany,
    totalAmount, donationAmount, cardLastFour,
    additionalSponsorships, teamMembers, dinnerGuests, logoUrl, specialRequests,
  } = receipt;

  const primaryLabel = sponsorshipLevel
    ? (SPONSORSHIP_LABELS[sponsorshipLevel] || sponsorshipLevel)
    : (REG_TYPE_LABELS[registrationType] || registrationType);

  const addOnTotal = additionalSponsorships.reduce((s, a) => s + Number(a.price || 0), 0);
  const baseAmount = totalAmount - addOnTotal - (donationAmount || 0);

  const allParticipants = [
    ...teamMembers.map(m => ({ name: `${m.firstName} ${m.lastName}`, role: m.isCaptain ? 'Primary Contact' : 'Golfer', ini: initials(m.firstName, m.lastName), color: m.isCaptain ? '#e51c22' : '#7f1d1d' })),
    ...dinnerGuests.map(g => ({ name: `${g.firstName} ${g.lastName}`, role: 'Dinner Guest', ini: initials(g.firstName, g.lastName), color: '#4b1818' })),
  ];

  const shareLabel: Record<ShareStatus, string> = {
    idle:    'SHARE',
    sharing: 'SENDING...',
    success: `SENT TO ${shareCount} RECIPIENT${shareCount !== 1 ? 'S' : ''} CHECK`,
    none:    'NO RECIPIENTS TO SHARE',
    error:   'SHARE FAILED — TRY AGAIN',
  };

  const shareColor: Record<ShareStatus, string> = {
    idle:    'linear-gradient(135deg, #e51c22, #b91c1c)',
    sharing: '#374151',
    success: 'linear-gradient(135deg, #16a34a, #15803d)',
    none:    '#374151',
    error:   '#7f1d1d',
  };

  return (
    <div style={S.page}>
      <div style={S.printBar} className="no-print">
        <button onClick={() => window.print()} style={S.printBtn}>Print / Save PDF</button>
      </div>

      <div style={S.mobileCard} className="receipt-shell">

        {/* cf-logo-3 */}
        <div style={S.topLogoWrap}>
          <img src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`} alt="Champion It Forward" style={S.cfLogo} />
        </div>

        {/* Checkmark */}
        <div style={S.checkCircle}>
          <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h1 style={S.title} className="receipt-title">Payment Confirmed</h1>
        <p style={S.subtitle} className="receipt-subtitle">Your payment has been successfully processed.</p>

        {logoUrl && (
          <div style={S.companyLogoWrap}>
            <img src={logoUrl} alt="Company Logo" style={S.companyLogo} />
          </div>
        )}

        <div className="receipt-main-grid">
          {/* Transaction Details */}
          <InfoCard icon="receipt" title="Transaction Details">
            <DetailRow label="Confirmation ID" value={<span style={S.monoVal}>{confNum}</span>} />
            <DetailRow label="Date & Time"     value={formatDateTime(registrationTimestamp)} />
            {captainCompany && <DetailRow label="Company" value={captainCompany} />}
            {teamName        && <DetailRow label="Team"    value={teamName} />}
            {cardLastFour
              ? <DetailRow label="Payment Method" value={`\u00B7\u00B7\u00B7\u00B7 ${cardLastFour}`} />
              : <DetailRow label="Payment Method" value="Invoice / Direct" />
            }
          </InfoCard>

          {/* Sponsorship Summary */}
          <InfoCard icon="trophy" title="Sponsorship Summary">
            <LineItem label={primaryLabel} amount={fmt(baseAmount)} />
            {additionalSponsorships.map((a, i) => (
              <LineItem key={i} label={a.name} amount={a.price ? fmt(Number(a.price)) : '---'} sub />
            ))}
            {donationAmount > 0 && <LineItem label="Additional Donation" amount={fmt(donationAmount)} sub />}
            <div style={S.totalSeparator} />
            <div style={S.totalRow}>
              <span style={S.totalLabel}>Total Paid</span>
              <span style={S.totalAmt}>{fmt(totalAmount)}</span>
            </div>
          </InfoCard>

          {/* Golfers & Guests */}
          {allParticipants.length > 0 && (
            <InfoCard icon="golf" title="Golfers & Guests" className="receipt-full-span">
              {allParticipants.map((p, i) => (
                <div key={i} style={S.participantRow}>
                  <div style={{ ...S.avatar, background: p.color }}>{p.ini}</div>
                  <div>
                    <div style={S.participantName}>{p.name}</div>
                    <div style={S.participantRole}>{p.role}</div>
                  </div>
                </div>
              ))}
            </InfoCard>
          )}

          {/* Event Details */}
          <InfoCard icon="pin" title="Event Details">
            <DetailRow label="Event"   value="Champions Classic 2026" />
            <DetailRow label="Date"    value="Friday, June 19, 2026" />
            <DetailRow label="Venue"   value="Bolingbrook Golf Club" />
            <DetailRow label="Address" value="2001 Rodeo Dr, Bolingbrook, IL 60490" />
            <DetailRow label="Start"   value="8:00 AM Shotgun Start" />
            <DetailRow label="Format"  value="Four-Person Scramble" />
          </InfoCard>

          {/* Receipt & Documentation */}
          <InfoCard icon="doc" title="Receipt and Documentation">
            <ActionRow icon="dl" label="Download Receipt (PDF)" onClick={() => window.print()} />
            <ActionRow
              icon="mail"
              label={`Email A Copy to ${captainEmail}`}
              onClick={() => {
                window.location.href = `mailto:${captainEmail}?subject=Champions Classic 2026 Receipt ${confNum}&body=View your receipt: ${window.location.href}`;
              }}
            />
          </InfoCard>
        </div>

        {/* Info Banner */}
        <div style={S.infoBanner}>
          <span style={{ fontSize: 18, marginRight: 10, flexShrink: 0 }}>i</span>
          <p style={S.bannerText}>
            A receipt was sent to <strong style={{ color: '#fca5a5' }}>{captainEmail}</strong>.
            Use <strong style={{ color: '#fca5a5' }}>SHARE</strong> to send this receipt to all golfers and dinner guests.
          </p>
        </div>

        {specialRequests && (
          <div style={S.specialWrap}>
            <span style={S.specialLabel}>Special Requests</span>
            <p style={S.specialText}>{specialRequests}</p>
          </div>
        )}

        {/* SHARE button */}
        <button
          style={{ ...S.shareBtn, background: shareColor[shareStatus] }}
          onClick={handleShare}
          disabled={shareStatus === 'sharing'}
        >
          {shareLabel[shareStatus]}
        </button>

        {/* Footer */}
        <div style={S.footer}>
          <img src={`${process.env.PUBLIC_URL}/assets/CrashChampions_Logo_White.png`} alt="Crash Champions" style={{ height: 22, opacity: 0.6 }} />
          <span style={S.footerDivider}>|</span>
          <img src={`${process.env.PUBLIC_URL}/assets/ChampionsClassicLogo-Final-White.png`} alt="Champions Classic" style={{ height: 30, opacity: 0.6 }} />
        </div>
        <p style={S.footerContact}>
          Questions? <a href="mailto:charityevent@crashchampions.com" style={{ color: '#e51c22' }}>charityevent@crashchampions.com</a>
        </p>

      </div>

      <style>{`
        ${spinKeyframes}
        .receipt-main-grid { width: 100%; }
        .receipt-detail-row { display: flex; }
        .receipt-action-row { min-height: 50px; }

        @media (min-width: 768px) {
          .receipt-shell { max-width: 920px !important; }
          .receipt-main-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
            align-items: start;
          }
          .receipt-full-span { grid-column: 1 / -1; }
        }

        @media (max-width: 420px) {
          .receipt-shell { padding-left: 10px !important; padding-right: 10px !important; }
          .receipt-title { font-size: 21px !important; }
          .receipt-subtitle { font-size: 12px !important; margin-bottom: 20px !important; }
          .receipt-detail-row {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 4px;
          }
        }

        @media print { .no-print { display: none !important; } body { background: #fff !important; } }
      `}</style>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────
const InfoCard: React.FC<{ icon: string; title: string; children: React.ReactNode; className?: string }> = ({ icon, title, children, className }) => (
  <div style={S.card} className={className}>
    <div style={S.cardHeader}>
      <span style={S.cardTitle}>{title}</span>
    </div>
    {children}
  </div>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={S.detailRow} className="receipt-detail-row">
    <span style={S.detailLabel}>{label}</span>
    <span style={S.detailValue}>{value}</span>
  </div>
);

const LineItem: React.FC<{ label: string; amount: string; sub?: boolean }> = ({ label, amount, sub }) => (
  <div style={{ ...S.lineItem, opacity: sub ? 0.8 : 1 }}>
    <span style={{ ...S.lineLabel, paddingLeft: sub ? 12 : 0, fontSize: sub ? 13 : 14 }}>
      {sub && <span style={{ color: '#4b5563', marginRight: 6 }}>+</span>}{label}
    </span>
    <span style={S.lineAmt}>{amount}</span>
  </div>
);

const ActionRow: React.FC<{ icon: string; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <button onClick={onClick} style={S.actionRow} className="receipt-action-row">
    <span style={S.actionLabel}>{label}</span>
    <span style={{ marginLeft: 'auto', color: '#4b5563', fontSize: 20 }}>›</span>
  </button>
);

// ────────────────────────────────────────────────────────────────────────────
// Animation
// ────────────────────────────────────────────────────────────────────────────
const spinKeyframes = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
`;

// ────────────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#050505',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 60,
    fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif",
  },
  centered: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh',
  },
  spinner: {
    width: 36, height: 36,
    border: '3px solid #1f1f1f',
    borderTop: '3px solid #e51c22',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  printBar: {
    width: '100%', maxWidth: 920, display: 'flex', justifyContent: 'flex-end',
    padding: '16px 16px 0',
  },
  printBtn: {
    background: 'transparent', border: '1px solid #1f1f1f', color: '#4b5563',
    padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
  },
  mobileCard: {
    width: '100%', maxWidth: 920,
    padding: '0 16px 32px',
    animation: 'fadeUp 0.5s ease-out',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  topLogoWrap: {
    marginTop: 28, marginBottom: 20,
    background: '#111',
    borderRadius: 16,
    padding: '14px 28px',
    border: '1px solid #1f1f1f',
  },
  cfLogo: { height: 50, objectFit: 'contain' as const },
  checkCircle: {
    width: 72, height: 72,
    background: 'linear-gradient(135deg, #e51c22, #b91c1c)',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 0 10px rgba(229,28,34,0.1), 0 0 0 20px rgba(229,28,34,0.05)',
    marginBottom: 20,
  },
  title: {
    fontSize: 24, fontWeight: 800, color: '#ffffff',
    margin: '0 0 8px', textAlign: 'center' as const,
  },
  subtitle: {
    fontSize: 13, color: '#6b7280', margin: '0 0 28px', textAlign: 'center' as const,
  },
  companyLogoWrap: {
    background: '#fff', borderRadius: 12, padding: '10px 20px',
    marginBottom: 20, display: 'inline-flex',
  },
  companyLogo: { height: 48, objectFit: 'contain' as const },
  card: {
    width: '100%',
    background: '#111',
    borderRadius: 16,
    border: '1px solid #1f1f1f',
    marginBottom: 12,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 18px 12px',
    borderBottom: '1px solid #1a1a1a',
  },
  cardTitle: {
    fontSize: 13, fontWeight: 700, color: '#e5e7eb', letterSpacing: '0.04em',
  },
  detailRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '10px 18px',
    borderBottom: '1px solid #0f0f0f',
  },
  detailLabel: { fontSize: 13, color: '#6b7280', flex: '0 0 auto', marginRight: 12 },
  detailValue: { fontSize: 13, color: '#e5e7eb', textAlign: 'right' as const, wordBreak: 'break-word' as const },
  monoVal: { fontFamily: 'monospace', fontSize: 14, color: '#fff', fontWeight: 700, letterSpacing: '0.05em' },
  lineItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 18px',
  },
  lineLabel: { fontSize: 14, color: '#d1d5db' },
  lineAmt:   { fontSize: 14, color: '#e5e7eb', fontWeight: 600 },
  totalSeparator: { height: 1, background: '#1f1f1f', margin: '4px 18px' },
  totalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 18px 14px',
  },
  totalLabel: { fontSize: 12, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' as const },
  totalAmt:   { fontSize: 26, fontWeight: 800, color: '#ffffff' },
  participantRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 18px',
    borderBottom: '1px solid #0f0f0f',
  },
  avatar: {
    width: 38, height: 38, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  participantName: { fontSize: 14, color: '#e5e7eb', fontWeight: 600 },
  participantRole: { fontSize: 11, color: '#6b7280', marginTop: 2 },
  actionRow: {
    display: 'flex', alignItems: 'center', width: '100%',
    padding: '14px 18px', background: 'transparent', border: 'none',
    borderBottom: '1px solid #0f0f0f', cursor: 'pointer',
    textAlign: 'left' as const,
  },
  actionLabel: { fontSize: 14, color: '#e5e7eb' },
  infoBanner: {
    width: '100%',
    background: 'rgba(127,29,29,0.2)',
    border: '1px solid rgba(229,28,34,0.2)',
    borderRadius: 12,
    padding: '14px 16px',
    display: 'flex', alignItems: 'flex-start', gap: 4,
    marginBottom: 12,
  },
  bannerText: {
    fontSize: 13, color: '#9ca3af', lineHeight: 1.6, margin: 0,
  },
  specialWrap: {
    width: '100%', background: '#111', borderRadius: 12, border: '1px solid #1f1f1f',
    padding: '14px 18px', marginBottom: 12,
  },
  specialLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.15em',
    color: '#e51c22', textTransform: 'uppercase' as const, display: 'block', marginBottom: 8,
  },
  specialText: { fontSize: 13, color: '#9ca3af', lineHeight: 1.6, margin: 0 },
  shareBtn: {
    width: '100%', padding: '18px',
    border: 'none', borderRadius: 14, cursor: 'pointer',
    fontSize: 15, fontWeight: 800, color: '#fff',
    letterSpacing: '0.08em', marginTop: 8, marginBottom: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'background 0.3s ease',
    minHeight: 56,
  },
  footer: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
  },
  footerDivider: { color: '#1f1f1f', fontSize: 18 },
  footerContact: {
    fontSize: 12, color: '#374151', textAlign: 'center' as const, margin: 0,
  },
};

export default ReceiptPage;
